// MTXbudget: the amounts the banks never send — a bill, the minimum of a card
// statement, the balance of an account not read this week — asked of the user
// when they are needed, and taken by the plan once given.
import { addDays, weekStart } from './mtxbudget';
import type { Occurrence } from './mtxbudget';

/** How far ahead an estimated amount is asked for. */
export const COMPLETE_DAYS = 14;
/** How long « Plus tard » puts an item to sleep, at most. */
export const LATER_DAYS = 3;

/** An amount the user gave, in cents with the sign of what it replaces. */
export type Provided = { amount: number; on: string };

export type ToComplete =
  | {
      kind: 'amount';
      id: string;
      name: string;
      date: string;
      /** The estimate in use, in cents, as a positive amount to pay. */
      estimate: number;
      account?: string;
      /** The minimum of a card statement, rather than a bill. */
      statement: boolean;
    }
  | {
      kind: 'balance';
      id: string;
      accountId: string;
      name: string;
      /** The balance last known, in cents. */
      estimate: number;
    };

/** Each occurrence with the amount the user gave for it, if any. */
export function withProvided(
  occurrences: readonly Occurrence[],
  provided: Readonly<Record<string, Provided>>,
): Occurrence[] {
  return occurrences.map(occurrence => {
    const given = provided[occurrence.id];
    return given
      ? { ...occurrence, amount: given.amount, approximate: false }
      : occurrence;
  });
}

/** A bill the creditor lets be paid in part: `now` on its day, the rest later. */
export type Arrangement = { now: number; restOn: string; accepted?: boolean };

/**
 * Each charge as arranged with its creditor: what is paid on its day, and
 * the rest on the day agreed. A charge with no arrangement stays as it is.
 */
export function withArrangements(
  occurrences: readonly Occurrence[],
  arrangements: Readonly<Record<string, Arrangement>>,
): Occurrence[] {
  return occurrences.flatMap(occurrence => {
    const arranged = arrangements[occurrence.id];
    const total = -occurrence.amount;
    if (!arranged || arranged.now >= total) {
      return [occurrence];
    }
    const now = Math.max(0, arranged.now);
    return [
      ...(now > 0 ? [{ ...occurrence, amount: -now, approximate: false }] : []),
      {
        ...occurrence,
        id: `${occurrence.id}/reste`,
        name: `${occurrence.name} (reste)`,
        date: arranged.restOn,
        amount: -(total - now),
        approximate: false,
        recurring: false,
      },
    ];
  });
}

/**
 * What to ask the user for now: the balance of each cash account its bank
 * sent nothing for since Sunday, then each estimated charge due within the
 * next fortnight, soonest first. What was given, or put off, is left out.
 */
export function toComplete({
  occurrences,
  today,
  provided,
  providedBalances,
  later,
  statementScheduleIds,
  unread,
}: {
  occurrences: readonly Occurrence[];
  today: string;
  provided: Readonly<Record<string, Provided>>;
  providedBalances: Readonly<Record<string, Provided>>;
  later: Readonly<Record<string, string>>;
  /** Schedules that pay a card or a loan: their amount is a minimum. */
  statementScheduleIds: ReadonlySet<string>;
  /** Cash accounts whose bank sent nothing since Sunday. */
  unread: readonly { accountId: string; name: string; balance: number }[];
}): ToComplete[] {
  const asleep = (id: string) => later[id] !== undefined && later[id] > today;
  const horizon = addDays(today, COMPLETE_DAYS);
  const balances: ToComplete[] = unread
    .filter(account => {
      const given = providedBalances[account.accountId];
      return (
        !(given && given.on >= weekStart(today)) &&
        !asleep(`balance/${account.accountId}`)
      );
    })
    .map(account => ({
      kind: 'balance',
      id: `balance/${account.accountId}`,
      accountId: account.accountId,
      name: account.name,
      estimate: account.balance,
    }));
  const amounts: ToComplete[] = occurrences
    .filter(
      o =>
        o.amount < 0 &&
        o.approximate &&
        o.date >= today &&
        o.date <= horizon &&
        provided[o.id] === undefined &&
        !asleep(o.id),
    )
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(o => ({
      kind: 'amount',
      id: o.id,
      name: o.name,
      date: o.date,
      estimate: -o.amount,
      account: o.account,
      statement:
        o.scheduleId !== undefined && statementScheduleIds.has(o.scheduleId),
    }));
  return [...balances, ...amounts];
}

/**
 * Until when « Plus tard » puts an item to sleep: three days, or the eve of
 * the day it is due when that comes sooner, never before tomorrow.
 */
export function laterUntil(today: string, due?: string): string {
  const inThree = addDays(today, LATER_DAYS);
  const eve = due ? addDays(due, -1) : inThree;
  const until = eve < inThree ? eve : inThree;
  const tomorrow = addDays(today, 1);
  return until < tomorrow ? tomorrow : until;
}

/**
 * An amount as typed in French or English — « 285,58 », « 1 234,56 $ »,
 * « -257,15 » — in cents. Null when it is not one.
 */
export function parseAmount(text: string): number | null {
  const cleaned = text.replace(/[\s  $]/g, '').replace(/^[−–]/, '-');
  if (!/^-?\d+([.,]\d{1,2})?$/.test(cleaned)) {
    return null;
  }
  return Math.round(Number(cleaned.replace(',', '.')) * 100);
}
